import { API_BASE } from './apiBase';

export interface DirectionsLineString {
  type: 'LineString';
  coordinates: Array<[number, number]>;
}

interface DirectionsResponse {
  geometry: DirectionsLineString;
}

// In-memory cache to avoid re-fetching the same sequence within a session, even if the
// worker (KV) cache is available. Keyed by canonical coords string.
const memoryCache = new Map<string, DirectionsLineString>();
const inflight = new Map<string, Promise<DirectionsLineString | null>>();

const cacheKey = (coords: Array<[number, number]>): string =>
  coords.map(([lng, lat]) => `${lng.toFixed(5)},${lat.toFixed(5)}`).join(';');

export const fetchDirections = async (
  coords: Array<[number, number]>,
): Promise<DirectionsLineString | null> => {
  if (coords.length < 2) return null;
  const key = cacheKey(coords);
  const cached = memoryCache.get(key);
  if (cached) return cached;
  const existing = inflight.get(key);
  if (existing) return existing;

  const promise = (async (): Promise<DirectionsLineString | null> => {
    try {
      const res = await fetch(`${API_BASE}/api/directions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coordinates: coords }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as DirectionsResponse;
      memoryCache.set(key, data.geometry);
      return data.geometry;
    } catch {
      return null;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
};
